import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyProfile, getMyWallet, getMyOrders } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import LoadingIndicator from "../../components/LoadingIndicator";

const ACTIVE_STATUSES = ["scheduled", "requested", "accepted", "arrived_at_pickup", "picked_up", "in_transit"];

const STATUS_ICON = {
  scheduled: "⏰", requested: "📡", accepted: "🛵", arrived_at_pickup: "📍",
  picked_up: "📦", in_transit: "🚀", delivered: "✅", completed: "✅",
  cancelled: "❌", failed: "❌",
};

export default function UserDashboard() {
  const { account } = useAuth();
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getMyProfile(), getMyWallet(), getMyOrders()])
      .then(([pRes, wRes, oRes]) => {
        setProfile(pRes.data);
        setWallet(wRes.data);
        setOrders(oRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeOrders = orders.filter(o => ACTIVE_STATUSES.includes(o.status));
  const completedCount = orders.filter(o => ["delivered", "completed"].includes(o.status)).length;
  const recentOrders = orders.slice(0, 5);

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          walletBalance={wallet?.balance ?? null}
          membershipTier={profile?.membershipTier || "standard"}
        />

        <div className="welcome-banner">
          <h2>Welcome back, {account?.name?.split(" ")[0] || "there"} 👋</h2>
          <p>Here's what's happening with your deliveries.</p>
        </div>

        <div className="page page-with-nav" style={{ marginTop: "16px" }}>
          {loading ? (
            <LoadingIndicator label="Loading your dashboard..." />
          ) : (
            <>
              {/* STAT TILES */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
                <div className="quick-card">
                  <span className="quick-card-icon">💰</span>
                  <span className="quick-card-title">Wallet</span>
                  <strong style={{ fontSize: "18px", color: "var(--text-accent)" }}>₦{(wallet?.balance || 0).toLocaleString()}</strong>
                </div>
                <div className="quick-card">
                  <span className="quick-card-icon">🔴</span>
                  <span className="quick-card-title">Active</span>
                  <strong style={{ fontSize: "18px" }}>{activeOrders.length}</strong>
                </div>
                <div className="quick-card">
                  <span className="quick-card-icon">✅</span>
                  <span className="quick-card-title">Completed</span>
                  <strong style={{ fontSize: "18px" }}>{completedCount}</strong>
                </div>
              </div>

              {/* MEMBERSHIP STATUS */}
              {profile?.membershipTier && profile.membershipTier !== "standard" ? (
                <div className="surge-toggle" style={{ background: "rgba(22, 199, 132, 0.1)", borderColor: "rgba(22, 199, 132, 0.3)", marginBottom: "16px" }}>
                  <span style={{ color: "var(--success)", fontWeight: 600, fontSize: "14px" }}>
                    {profile.membershipTier === "plus" ? "⭐ FASTSEND Plus Active — 20% off every order" : "💚 Saver Plan Active — up to 18% off this month"}
                  </span>
                </div>
              ) : (
                <Link to="/subscription" style={{ textDecoration: "none" }}>
                  <div className="surge-toggle" style={{ marginBottom: "16px", cursor: "pointer" }}>
                    <span style={{ fontSize: "14px" }}>⭐ Save on every delivery — explore Plus or Saver plans</span>
                  </div>
                </Link>
              )}

              {/* ACTIVE DELIVERIES */}
              {activeOrders.length > 0 && (
                <>
                  <h3>Active Deliveries</h3>
                  {activeOrders.map(order => (
                    <Link key={order._id} to={`/track/${order._id}`} style={{ textDecoration: "none" }}>
                      <div className="delivery-card" style={{ marginBottom: "12px", borderLeft: "3px solid var(--text-accent)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <strong>{STATUS_ICON[order.status]} #{order._id.slice(-6).toUpperCase()}</strong>
                            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                              {order.pickup.address.slice(0, 28)}... → {order.dropoff.address.slice(0, 28)}...
                            </p>
                          </div>
                          <span className="status-badge info">{order.status.replace(/_/g, " ")}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </>
              )}

              {/* QUICK ACTIONS */}
              <h3 style={{ marginTop: activeOrders.length > 0 ? "20px" : 0 }}>Quick Actions</h3>
              <div className="quick-cards" style={{ marginBottom: "20px" }}>
                <Link to="/order" className="quick-card">
                  <span className="quick-card-icon">📦</span>
                  <span className="quick-card-title">Send Package</span>
                  <span className="quick-card-desc">Get a quote &amp; book a dispatcher</span>
                </Link>
                <Link to="/wallet" className="quick-card">
                  <span className="quick-card-icon">💰</span>
                  <span className="quick-card-title">Wallet</span>
                  <span className="quick-card-desc">Top up &amp; view transactions</span>
                </Link>
                <Link to="/subscription" className="quick-card">
                  <span className="quick-card-icon">⭐</span>
                  <span className="quick-card-title">Plans</span>
                  <span className="quick-card-desc">FASTSEND Plus or Saver</span>
                </Link>
                <Link to="/profile" className="quick-card">
                  <span className="quick-card-icon">👤</span>
                  <span className="quick-card-title">Profile</span>
                  <span className="quick-card-desc">Edit details &amp; password</span>
                </Link>
              </div>

              {/* RECENT ORDERS */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3>Recent Orders</h3>
                <Link to="/orders" style={{ fontSize: "13px", color: "var(--text-accent)", textDecoration: "none" }}>View All →</Link>
              </div>
              {recentOrders.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state-icon">📭</span>
                  <p className="empty-state-text">You haven't placed any orders yet.</p>
                  <Link to="/order"><button style={{ marginTop: "12px" }}>Send a Package</button></Link>
                </div>
              ) : (
                recentOrders.map(order => (
                  <Link key={order._id} to={`/track/${order._id}`} style={{ textDecoration: "none" }}>
                    <div className="history-row" style={{ marginBottom: "10px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong style={{ fontSize: "13px" }}>#{order._id.slice(-6).toUpperCase()}</strong>
                          <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <strong style={{ fontSize: "14px", color: "var(--text-accent)", display: "block" }}>
                            ₦{order.priceSnapshot?.total?.toLocaleString()}
                          </strong>
                          <span className={`status-badge ${["cancelled", "failed"].includes(order.status) ? "danger" : ""}`} style={{ fontSize: "11px" }}>
                            {STATUS_ICON[order.status]} {order.status.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
